import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import classnames from "classnames"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Table,
  TabContent,
  TabPane,
  UncontrolledButtonDropdown,
} from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import DataCard from "components/Common/DataCard"
import SectionHeader from "components/Common/SectionHeader"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import {
  usePreOrder,
  useCancelPreOrder,
  useCreateMoneyReceiptFromPreOrder,
  useEditPreOrder,
} from "hooks/usePreOrder"
import {
  useCreateAccountCustomerPayment,
  useEditAccountCustomerPayment,
  useDeleteAccountCustomerPayment,
} from "hooks/useAccountCustomerPayment"
import { useToastMessage } from "hooks/useToastMessage"
import PrintDateModal from "pages/MoneyReceiptPages/PrintDateModal"
import GenerateMoneyReceiptModal from "pages/CustomerPages/CustomerProfile/PreOrder/GenerateMoneyReceiptModal"
import PreOrderPaymentModal from "./PreOrderPaymentModal"
import PreOrderSectionEditModal from "./PreOrderSectionEditModal"
import "pages/MoneyReceiptPages/MoneyReceiptView.css"

const TAB_ITEMS = [{ id: "payments", label: "Payments", icon: "bx bx-credit-card" }]

const getDisplayText = value => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object")
    return value?.name || value?.title || value?.label || ""
  return String(value)
}

const buildCustomerRows = preOrder => {
  const customerPdf = preOrder?.pdf_content?.customer_info || {}
  const customer = preOrder?.customer || {}

  return [
    { label: "Name", value: customerPdf.name || customer.name || "-" },
    { label: "ID", value: customer.cuid || "-" },
    { label: "Mobile", value: customerPdf.mobile || customer.mobile || "-" },
    { label: "Email", value: customerPdf.email || customer.email || "-" },
    {
      label: "Address",
      value: customerPdf.address || customer.full_address || "-",
    },
  ]
}

const buildVehicleRows = preOrder => {
  const vehiclePdf = preOrder?.pdf_content?.vehicle_info || {}
  const vehicle = preOrder?.vehicle || null

  return [
    { label: "Name", value: vehiclePdf.name || "-" },
    { label: "Stock ID", value: vehiclePdf.stock_id || vehicle?.stock_id || "-" },
    {
      label: "Chassis",
      value: vehiclePdf.full_chassis || vehicle?.full_chassis || "-",
    },
    {
      label: "Engine",
      value: vehiclePdf.full_engine || vehicle?.full_engine || "-",
    },
    { label: "Year", value: vehiclePdf.year_model || "-" },
    { label: "Color", value: vehiclePdf.color || "-" },
    { label: "CC", value: vehiclePdf.cc || "-" },
    { label: "Fuel", value: vehiclePdf.fuel || "-" },
    {
      label: "Price",
      value: preOrder?.car_price ? formatBDT(preOrder.car_price) : "-",
    },
    {
      label: "Registration Cost",
      value: preOrder?.registration_cost
        ? formatBDT(preOrder.registration_cost)
        : "-",
    },
  ]
}

const PreOrderViewPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { handleResponse, handleError } = useToastMessage()

  const [activeTab, setActiveTab] = useState("payments")
  const [printDateOpen, setPrintDateOpen] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [receiptModal, setReceiptModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState(null)
  const [deletePaymentModal, setDeletePaymentModal] = useState(null)
  const [sectionEdit, setSectionEdit] = useState(null)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [paymentPrintModal, setPaymentPrintModal] = useState(null)
  const [paymentPdfKey, setPaymentPdfKey] = useState(null)

  const { data: preOrderResponse, isLoading, refetch } = usePreOrder(id)
  const preOrder = preOrderResponse?.data ?? {}

  const { mutateAsync: cancelPreOrder, isPending: isCancelling } =
    useCancelPreOrder()
  const { mutateAsync: editPreOrder, isPending: savingSection } =
    useEditPreOrder()
  const { mutateAsync: createMoneyReceipt, isPending: creatingReceipt } =
    useCreateMoneyReceiptFromPreOrder()
  const { mutateAsync: createPayment, isPending: creatingPayment } =
    useCreateAccountCustomerPayment()
  const { mutateAsync: editPayment, isPending: editingPayment } =
    useEditAccountCustomerPayment()
  const { mutateAsync: deletePayment, isPending: deletingPayment } =
    useDeleteAccountCustomerPayment()

  const customerRows = useMemo(() => buildCustomerRows(preOrder), [preOrder])
  const vehicleRows = useMemo(() => buildVehicleRows(preOrder), [preOrder])
  const payments = preOrder?.payments ?? []
  const isCancelled = preOrder?.status === "cancelled"
  const hasMoneyReceipt = Number(preOrder?.money_receipts_count ?? 0) > 0
  const linkedMoneyReceipt = preOrder?.money_receipts?.[0] ?? null
  const canGenerateReceipt =
    Boolean(preOrder?.vehicle_id) && !isCancelled && !hasMoneyReceipt

  const generateReceiptBlockReason = (() => {
    if (hasMoneyReceipt) {
      return "A money receipt has already been generated for this pre-order."
    }
    if (isCancelled) {
      return "This pre-order is cancelled. Money receipts can only be generated for active pre-orders."
    }
    if (!preOrder?.vehicle_id) {
      return "No vehicle is matched to this pre-order yet. Add a chassis or engine number (via Edit on Vehicle Information) so a vehicle is reserved, then you can generate a money receipt."
    }
    return ""
  })()

  const lockedReason = hasMoneyReceipt
    ? "A money receipt has already been generated for this pre-order. Its deposit payments now carry forward onto that money receipt. Edit, cancellation, and payments are locked here — manage them from the money receipt instead."
    : ""

  const handlePrintDateConfirm = async printDate => {
    setPrintDateOpen(false)
    setIsPrinting(true)
    try {
      await downloadAndOpenFile({
        url: `/pre-orders/${id}/pdf`,
        params: { print_date: printDate || "" },
      })
    } catch (error) {
      handleError(error)
    } finally {
      setIsPrinting(false)
    }
  }

  const handleCancel = async () => {
    setCancelModal(false)
    try {
      const response = await cancelPreOrder(id)
      handleResponse(response, "Pre-order cancelled successfully.", true)
      refetch()
    } catch (error) {
      handleError(error, "Failed to cancel pre-order.")
    }
  }

  const handleGenerateReceipt = async payload => {
    try {
      const response = await createMoneyReceipt(payload)
      handleResponse(
        response,
        "Money receipt generated successfully. This pre-order is now locked — edit, cancellation, and payments must be managed from the money receipt.",
        true
      )
      refetch()
      const moneyReceiptId = response?.data?.id
      if (moneyReceiptId) {
        navigate(`/money-receipts/${moneyReceiptId}`)
      }
    } catch (error) {
      handleError(error, "Failed to generate money receipt.")
      throw error
    }
  }

  const handlePaymentSubmit = async payload => {
    try {
      const response = payload.id
        ? await editPayment(payload)
        : await createPayment(payload)
      handleResponse(
        response,
        `Payment ${payload.id ? "updated" : "added"} successfully.`,
        true
      )
      refetch()
    } catch (error) {
      handleError(error, "Failed to save payment.")
      throw error
    }
  }

  const handlePaymentDelete = async () => {
    const payment = deletePaymentModal
    setDeletePaymentModal(null)
    try {
      const response = await deletePayment({ id: payment.id })
      handleResponse(response, "Payment deleted successfully.", true)
      refetch()
    } catch (error) {
      handleError(error, "Failed to delete payment.")
    }
  }

  const openPaymentPrintModal = payment => {
    if (paymentPdfKey !== null) return
    setPaymentPrintModal({
      paymentId: payment.id,
      defaultDate: payment?.received_date,
    })
  }

  const closePaymentPrintModal = () => {
    setPaymentPrintModal(null)
  }

  const handlePaymentPrintConfirm = async printDate => {
    const target = paymentPrintModal
    setPaymentPrintModal(null)
    if (!target) return
    setPaymentPdfKey(target.paymentId)
    try {
      await downloadAndOpenFile({
        url: `/account-customer-payments/${target.paymentId}/receipt`,
        params: { print_date: printDate || "" },
      })
    } catch (error) {
      handleError(error, "Failed to download payment receipt.")
    } finally {
      setPaymentPdfKey(null)
    }
  }

  const handleSectionSave = async payload => {
    try {
      const response = await editPreOrder({ id, ...payload })
      handleResponse(response, "Pre-order updated successfully.", true)
      setSectionEdit(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to update pre-order.")
      throw error
    }
  }

  const sectionEditAction = sectionType =>
    !isCancelled && !hasMoneyReceipt ? (
      <CanView access={[ACCESS.PRE_ORDER.EDIT]}>
        <Button
          color="light"
          size="sm"
          onClick={() => setSectionEdit(sectionType)}
        >
          <i className="bx bx-edit-alt me-1" />
          Edit
        </Button>
      </CanView>
    ) : null

  return (
    <PageWrapper
      title="Pre-Order"
      breadcrumbItem="Pre-Order View"
      cardBody={false}
    >
      {isLoading ? (
        <Card className="border-0 shadow-sm">
          <CardBody>Loading...</CardBody>
        </Card>
      ) : (
        <div className="row">
          <div className="col-lg-4 mb-4">
            <Card className="border-0 shadow-sm overflow-hidden mb-4">
              <div className="money-receipt-profile-card__cover">
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <h5 className="mb-1 text-dark">
                      {preOrder?.poid || "Pre-Order"}
                    </h5>
                    <p className="mb-0 text-muted">
                      Booking, deposit, and document overview
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge
                      className={`badge-soft-${isCancelled ? "danger" : "success"} border-0`}
                    >
                      {isCancelled ? "Cancelled" : "Booked"}
                    </Badge>
                    <CanView access={[ACCESS.PRE_ORDER.CANCEL]}>
                      <UncontrolledButtonDropdown
                        direction="start"
                        isOpen={Boolean(moreMenuOpen)}
                        toggle={() => setMoreMenuOpen(prev => !prev)}
                      >
                        <DropdownToggle
                          caret
                          color="light"
                          size="sm"
                          disabled={isCancelled || hasMoneyReceipt}
                          title="More actions"
                        >
                          <i className="bx bx-dots-horizontal-rounded" />
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem
                            onClick={() => {
                              setMoreMenuOpen(false)
                              setCancelModal(true)
                            }}
                            disabled={isCancelled || hasMoneyReceipt}
                          >
                            <i className="bx bx-x-circle me-1 text-danger" />
                            Cancel Pre-Order
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledButtonDropdown>
                    </CanView>
                  </div>
                </div>
              </div>

              <CardBody className="p-4">
                <div className="d-flex flex-wrap gap-2 mb-4">
                  <Button color="light" onClick={() => navigate("/pre-orders")}>
                    <i className="bx bx-arrow-back me-1" />
                    Back
                  </Button>

                  <CanView access={[ACCESS.PRE_ORDER.EDIT]}>
                    <Button
                      color="warning"
                      onClick={() => navigate(`/pre-orders/${id}/edit`)}
                      disabled={isCancelled || hasMoneyReceipt}
                      title={lockedReason || undefined}
                    >
                      <i className="bx bx-edit-alt me-1" />
                      Edit
                    </Button>
                  </CanView>

                  <CanView access={[ACCESS.PRE_ORDER.VIEW]}>
                    <Button
                      color="info"
                      onClick={() => setPrintDateOpen(true)}
                      disabled={isPrinting}
                    >
                      <i
                        className={`bx ${isPrinting ? "bx-loader bx-spin" : "bx-download"} me-1`}
                      />
                      {isPrinting ? "Generating..." : "PDF"}
                    </Button>
                  </CanView>

                  <CanView access={[ACCESS.PRE_ORDER.CREATE_MONEY_RECEIPT]}>
                    <Button
                      color="success"
                      onClick={() => setReceiptModal(true)}
                      disabled={!canGenerateReceipt}
                      title={
                        canGenerateReceipt
                          ? "Generate Money Receipt"
                          : generateReceiptBlockReason
                      }
                    >
                      <i className="bx bx-receipt me-1" />
                      Generate Receipt
                    </Button>
                  </CanView>
                </div>

                {generateReceiptBlockReason ? (
                  <Alert
                    color={isCancelled ? "danger" : hasMoneyReceipt ? "success" : "warning"}
                    className="mb-4 d-flex align-items-start gap-2"
                    fade={false}
                  >
                    <i
                      className={`bx ${
                        isCancelled
                          ? "bx-x-circle text-danger"
                          : hasMoneyReceipt
                            ? "bx-lock-alt text-success"
                            : "bx-info-circle text-warning"
                      } fs-5`}
                    />
                    <div>
                      <span className="fw-semibold">
                        {hasMoneyReceipt
                          ? "Pre-order locked"
                          : "Money receipt is unavailable"}
                      </span>
                      <div className="small">
                        {hasMoneyReceipt ? lockedReason : generateReceiptBlockReason}
                      </div>
                      {hasMoneyReceipt && linkedMoneyReceipt ? (
                        <Button
                          color="success"
                          size="sm"
                          className="mt-2"
                          onClick={() =>
                            navigate(`/money-receipts/${linkedMoneyReceipt.id}`)
                          }
                        >
                          <i className="bx bx-receipt me-1" />
                          View Money Receipt
                          {linkedMoneyReceipt.mr_no
                            ? ` — ${linkedMoneyReceipt.mr_no}`
                            : ""}
                        </Button>
                      ) : null}
                    </div>
                  </Alert>
                ) : null}

                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div className="money-receipt-mini-stat">
                      <small className="text-muted d-block">Total Deposit</small>
                      <span className="fw-semibold">
                        {formatBDT(preOrder?.deposit_amount)}
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="money-receipt-mini-stat">
                      <small className="text-muted d-block">Created</small>
                      <span className="fw-semibold">
                        {formatReadableDate(preOrder?.created_at, "MMM d, y")}
                      </span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <div className="d-grid gap-4">
              <DataCard
                title="Customer Information"
                icon="bx bx-user-circle"
                data={customerRows}
                action={sectionEditAction("customer")}
              />

              <DataCard
                title="Vehicle Information"
                icon="bx bx-car"
                data={vehicleRows}
                action={sectionEditAction("vehicle")}
              />

              <Card className="border-0 shadow-sm">
                <CardBody className="p-3">
                  <SectionHeader
                    icon="bx bx-note"
                    title="Notes"
                    action={sectionEditAction("notes")}
                  />
                  {preOrder?.notes ? (
                    <div className="money-receipt-note">{preOrder.notes}</div>
                  ) : (
                    <div className="text-muted small">
                      No notes added yet. Click Edit to add notes.
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="col-lg-8">
            <Nav pills className="nav-pills-custom mb-4 receipt-tab-nav">
              {TAB_ITEMS.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={classnames({ active: activeTab === tab.id })}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={`${tab.icon} me-2`} />
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="payments">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-3">
                    <SectionHeader
                      icon="bx bx-credit-card-front"
                      title="Payments"
                      action={
                        <CanView access={[ACCESS.ACCOUNT_CUSTOMER_PAYMENT.ADD]}>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setPaymentModal({})}
                            disabled={isCancelled || hasMoneyReceipt}
                            title={lockedReason || undefined}
                          >
                            <i className="bx bx-plus me-1" />
                            Add Payment
                          </Button>
                        </CanView>
                      }
                    />

                    <div className="money-receipt-payment-banner mb-3">
                      <div>
                        <small className="text-muted d-block">Total Deposit</small>
                        <span className="fw-semibold">
                          {formatBDT(preOrder?.deposit_amount)}
                        </span>
                      </div>
                    </div>

                    {payments.length ? (
                      <div className="table-responsive">
                        <Table className="align-middle mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Date</th>
                              <th>Method</th>
                              <th>Reference / Notes</th>
                              <th>Amount</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((payment, index) => (
                              <tr key={payment.id}>
                                <td>{index + 1}</td>
                                <td>
                                  {formatReadableDate(
                                    payment?.received_date,
                                    "MMM d, y"
                                  )}
                                </td>
                                <td>
                                  <div>{payment?.payment_method?.name ?? "-"}</div>
                                  <div className="small text-muted">
                                    {Number(payment?.amount || 0) < 0
                                      ? "Refund"
                                      : "Payment"}
                                  </div>
                                </td>
                                <td className="money-receipt-table-notes">
                                  {payment?.notes ||
                                    payment?.reference_number ||
                                    "-"}
                                </td>
                                <td
                                  className={
                                    Number(payment?.amount || 0) < 0
                                      ? "text-danger fw-semibold"
                                      : "text-success fw-semibold"
                                  }
                                >
                                  {formatBDT(payment?.amount)}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <CanView
                                      access={[
                                        ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST,
                                      ]}
                                    >
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        title="Download payment receipt"
                                        onClick={() => openPaymentPrintModal(payment)}
                                        disabled={paymentPdfKey !== null}
                                      >
                                        <i
                                          className={`bx ${
                                            paymentPdfKey === payment.id
                                              ? "bx-loader bx-spin"
                                              : "bx-download"
                                          }`}
                                        />
                                      </Button>
                                    </CanView>
                                    <CanView
                                      access={[ACCESS.ACCOUNT_CUSTOMER_PAYMENT.EDIT]}
                                    >
                                      <Button
                                        color="warning"
                                        size="sm"
                                        onClick={() => setPaymentModal(payment)}
                                        disabled={hasMoneyReceipt}
                                        title={lockedReason || undefined}
                                      >
                                        <i className="bx bx-pencil" />
                                      </Button>
                                    </CanView>
                                    <CanView
                                      access={[ACCESS.ACCOUNT_CUSTOMER_PAYMENT.DELETE]}
                                    >
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => setDeletePaymentModal(payment)}
                                        disabled={hasMoneyReceipt}
                                        title={lockedReason || undefined}
                                      >
                                        <i className="bx bx-trash" />
                                      </Button>
                                    </CanView>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="money-receipt-empty-state">
                        No payments recorded yet for this booking.
                      </div>
                    )}
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </div>
        </div>
      )}

      <PrintDateModal
        isOpen={printDateOpen}
        toggle={() => setPrintDateOpen(false)}
        onSubmit={handlePrintDateConfirm}
        isSubmitting={isPrinting}
        title="Pre-Order — Print Date"
        defaultDate={preOrder?.created_at}
      />

      <PrintDateModal
        isOpen={Boolean(paymentPrintModal)}
        toggle={closePaymentPrintModal}
        onSubmit={handlePaymentPrintConfirm}
        isSubmitting={paymentPdfKey !== null}
        title="Payment Receipt — Print Date"
        defaultDate={paymentPrintModal?.defaultDate}
      />

      {receiptModal ? (
        <GenerateMoneyReceiptModal
          isOpen={receiptModal}
          toggle={() => setReceiptModal(false)}
          preOrder={preOrder}
          onSubmit={handleGenerateReceipt}
          isSubmitting={creatingReceipt}
        />
      ) : null}

      <ConfirmModal
        isOpen={cancelModal}
        toggle={() => setCancelModal(false)}
        title="Cancel Pre-Order?"
        message="This will cancel the pre-order and release the reserved vehicle if no other active booking holds it. Do you want to proceed?"
        confirmLabel="Cancel Booking"
        icon="mdi-close-circle-outline"
        btnColor="warning"
        onConfirm={handleCancel}
        isConfirming={isCancelling}
      />

      {paymentModal ? (
        <PreOrderPaymentModal
          isOpen={!!paymentModal}
          toggle={() => setPaymentModal(null)}
          preOrder={preOrder}
          payment={paymentModal?.id ? paymentModal : null}
          onSubmit={handlePaymentSubmit}
          isSubmitting={creatingPayment || editingPayment}
        />
      ) : null}

      <DeleteModal
        show={!!deletePaymentModal}
        onDeleteClick={handlePaymentDelete}
        onCloseClick={() => setDeletePaymentModal(null)}
        loading={deletingPayment}
      />

      <PreOrderSectionEditModal
        isOpen={Boolean(sectionEdit)}
        toggle={() => setSectionEdit(null)}
        preOrder={preOrder}
        sectionType={sectionEdit}
        onSubmit={handleSectionSave}
        isSubmitting={savingSection}
      />
    </PageWrapper>
  )
}

export default PreOrderViewPage
